import React, { useState, createContext, useContext } from 'react';
type Language = 'uk' | 'ru';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const translations: Record<Language, Record<string, string>> = {
  uk: {
    // Navigation
    'nav.home': 'ГОЛОВНА',
    'nav.services': 'ПОСЛУГИ',
    'nav.portfolio': 'ПОРТФОЛІО',
    'nav.about': 'ПРО НАС',
    'nav.booking': 'ЗАПИС',
    'nav.contacts': 'КОНТАКТИ',
    // Hero
    'hero.title': 'СТУДІЯ',
    'hero.subtitle': 'індивідуального пошиву',
    'hero.cta1': 'ЗАПИСАТИСЯ',
    'hero.cta2': 'ОЦІНИТИ ВАРТІСТЬ',
    'hero.vertical.left': 'Premium Atelier',
    'hero.vertical.right': 'Vinnytsia • Ukraine',
    'hero.est': 'Est. 2019',
    'hero.scroll': 'Scroll',
    // Values
    'values.title': 'Чому обирають нас',
    'values.experience.title': 'ДОСВІД',
    'values.experience.desc': '5+ років бездоганної репутації у сфері високої моди.',
    'values.quality.title': 'ЯКІСТЬ',
    'values.quality.desc': 'Преміальні матеріали та увага до кожного шва.',
    'values.guarantee.title': 'ГАРАНТІЯ',
    'values.guarantee.desc': 'Повна відповідальність за результат вашого образу.',
    'values.service.title': 'СЕРВІС',
    'values.service.desc': 'Приватні примірки без черг та зайвих очей.',
    // Services
    'services.title': 'НАШІ ПОСЛУГИ',
    'services.subtitle': 'Our Expertise',
    'services.1.title': 'ІНДИВІДУАЛЬНИЙ ПОШИВ',
    'services.1.desc': 'Створення унікального одягу, що ідеально пасує вашій фігурі. Від ескізу до готового виробу.',
    'services.2.title': 'КОРЕКЦІЯ ПОСАДКИ',
    'services.2.desc': 'Професійна підгонка брендового одягу. Зробимо так, ніби річ була пошита для вас.',
    'services.3.title': 'ДЕЛІКАТНИЙ РЕМОНТ',
    'services.3.desc': 'Відновлення улюблених речей, реставрація трикотажу та складний ремонт.',
    'services.details': 'ДЕТАЛЬНІШЕ',
    // Process
    'process.title': 'ПРОЦЕС',
    'process.intro': 'Ми перетворили складний процес пошиву на елегантну подорож до вашого ідеального образу.',
    'process.step1.label': 'Крок 1',
    'process.step1.title': 'ЗАПИС',
    'process.step1.desc': 'Консультація та обговорення ваших побажань',
    'process.step2.label': 'Крок 2',
    'process.step2.title': 'РОБОТА',
    'process.step2.desc': 'Пошив або ремонт з увагою до деталей',
    'process.step3.label': 'Крок 3',
    'process.step3.title': 'ПРИМІРКА',
    'process.step3.desc': 'Фінальна корекція та підгонка',
    'process.step4.label': 'Крок 4',
    'process.step4.title': 'РЕЗУЛЬТАТ',
    'process.step4.desc': 'Отримання ідеального виробу',
    // Portfolio
    'portfolio.label': 'PORTFOLIO',
    'portfolio.title': 'НАШІ РОБОТИ',
    'portfolio.all': 'ВСІ',
    'portfolio.tailoring': 'ПОШИВ',
    'portfolio.fitting': 'ПОСАДКА',
    'portfolio.restoration': 'ВІДНОВЛЕННЯ',
    'portfolio.viewAll': 'ПЕРЕГЛЯНУТИ ВСІ',
    // Philosophy
    'philosophy.label': 'PHILOSOPHY',
    'philosophy.title': 'МИСТЕЦТВО',
    'philosophy.subtitle': 'деталей',
    'philosophy.text1': '«На 5 поверхів» — це простір тиші та естетики серед міського шуму. Ми не просто шиємо одяг, ми створюємо відчуття впевненості та комфорту.',
    'philosophy.text2': 'Кожен стібок — це діалог між майстром та тканиною. Ми віримо в повільну моду, якість, що не підвладна часу, та індивідуальність, яку неможливо скопіювати.',
    'philosophy.founder': 'Аліна',
    'philosophy.role': 'Засновниця & Креативна Директорка',
    // Service Details
    'details.title': 'ДЕТАЛЬНІШЕ ПРО ПОСЛУГИ',
    'details.bespoke': 'ІНДИВІДУАЛЬНИЙ ПОШИВ',
    'details.alterations': 'КОРЕКЦІЯ ПОСАДКИ',
    'details.restoration': 'РЕМОНТ ТА РЕСТАВРАЦІЯ',
    'details.bespoke.1': 'Вечірні сукні',
    'details.bespoke.2': 'Верхній одяг',
    'details.bespoke.3': 'Ділові костюми',
    'details.bespoke.4': 'Вироби зі шкіри',
    'details.alterations.1': 'Підгонка по фігурі',
    'details.alterations.2': 'Зміна довжини',
    'details.alterations.3': 'Корекція посадки',
    'details.alterations.4': 'Заміна фурнітури',
    'details.restoration.1': 'Реставрація вінтажу',
    'details.restoration.2': 'Ремонт трикотажу',
    'details.restoration.3': 'Відновлення швів',
    'details.restoration.4': 'Заміна підкладки',
    // Testimonials
    'testimonials.label': 'TESTIMONIALS',
    'testimonials.title': 'ДОВІРА КЛІЄНТІВ',
    'testimonials.1.text': 'Бездоганна робота. Сукня сіла ідеально, наче друга шкіра.',
    'testimonials.1.author': 'ОЛЕНА К.',
    'testimonials.1.role': 'Постійний клієнт',
    'testimonials.2.text': 'Врятували мій улюблений жакет. Справжня магія.',
    'testimonials.2.author': 'МАРИНА В.',
    'testimonials.2.role': 'Клієнт',
    'testimonials.3.text': 'Найвищий рівень сервісу у місті. Приватно та професійно.',
    'testimonials.3.author': 'ІРИНА М.',
    'testimonials.3.role': 'VIP Клієнт',
    // Contact
    'contact.title': 'ЯК НАС ЗНАЙТИ',
    'contact.address.title': 'Адреса',
    'contact.address.full': 'вул. 600-річчя, 15, кабінет 1, 5 поверх',
    'contact.address.city': 'м. Вінниця, Вінницька область, 21021',
    'contact.phone.title': 'Телефон',
    'contact.phone.number': '+38 (097) 000 00 00',
    'contact.schedule.title': 'Графік роботи',
    'contact.schedule.weekdays': 'Пн-Пт: 08:30 - 19:00',
    'contact.schedule.saturday': 'Сб: 15:00 - 21:00',
    'contact.schedule.sunday': 'Нд: Вихідний',
    'contact.email.title': 'Email',
    'contact.form.title': 'Записатися на примірку',
    'contact.form.name': "Ім'я",
    'contact.form.phone': 'Телефон',
    'contact.form.message': 'Повідомлення',
    'contact.form.submit': 'ВІДПРАВИТИ',
    'contact.form.name.placeholder': "Ваше ім'я",
    'contact.form.phone.placeholder': '+38 (___) ___ __ __',
    'contact.form.message.placeholder': 'Опишіть ваші побажання...',
    // Footer
    'footer.tagline': 'PREMIUM ATELIER',
    'footer.copyright': '© 2019-2024 На 5 поверхів. Всі права захищені.'
  },
  ru: {
    // Navigation
    'nav.home': 'ГЛАВНАЯ',
    'nav.services': 'УСЛУГИ',
    'nav.portfolio': 'ПОРТФОЛИО',
    'nav.about': 'О НАС',
    'nav.booking': 'ЗАПИСЬ',
    'nav.contacts': 'КОНТАКТЫ',
    // Hero
    'hero.title': 'СТУДИЯ',
    'hero.subtitle': 'индивидуального пошива',
    'hero.cta1': 'ЗАПИСАТЬСЯ',
    'hero.cta2': 'ОЦЕНИТЬ СТОИМОСТЬ',
    'hero.vertical.left': 'Premium Atelier',
    'hero.vertical.right': 'Vinnytsia • Ukraine',
    'hero.est': 'Est. 2019',
    'hero.scroll': 'Scroll',
    // Values
    'values.title': 'Почему выбирают нас',
    'values.experience.title': 'ОПЫТ',
    'values.experience.desc': '5+ лет безупречной репутации в сфере высокой моды.',
    'values.quality.title': 'КАЧЕСТВО',
    'values.quality.desc': 'Премиальные материалы и внимание к каждому шву.',
    'values.guarantee.title': 'ГАРАНТИЯ',
    'values.guarantee.desc': 'Полная ответственность за результат вашего образа.',
    'values.service.title': 'СЕРВИС',
    'values.service.desc': 'Приватные примерки без очередей и лишних глаз.',
    // Services
    'services.title': 'НАШИ УСЛУГИ',
    'services.subtitle': 'Our Expertise',
    'services.1.title': 'ИНДИВИДУАЛЬНЫЙ ПОШИВ',
    'services.1.desc': 'Создание уникальной одежды, которая идеально садится по вашей фигуре. От эскиза до готового изделия.',
    'services.2.title': 'КОРРЕКЦИЯ ПОСАДКИ',
    'services.2.desc': 'Профессиональная подгонка брендовой одежды. Сделаем так, будто вещь была сшита для вас.',
    'services.3.title': 'ДЕЛИКАТНЫЙ РЕМОНТ',
    'services.3.desc': 'Восстановление любимых вещей, реставрация трикотажа и сложный ремонт.',
    'services.details': 'ПОДРОБНЕЕ',
    // Process
    'process.title': 'ПРОЦЕСС',
    'process.intro': 'Мы превратили сложный процесс пошива в элегантное путешествие к вашему идеальному образу.',
    'process.step1.label': 'Шаг 1',
    'process.step1.title': 'ЗАПИСЬ',
    'process.step1.desc': 'Консультация и обсуждение ваших пожеланий',
    'process.step2.label': 'Шаг 2',
    'process.step2.title': 'РАБОТА',
    'process.step2.desc': 'Пошив или ремонт с вниманием к деталям',
    'process.step3.label': 'Шаг 3',
    'process.step3.title': 'ПРИМЕРКА',
    'process.step3.desc': 'Финальная коррекция и подгонка',
    'process.step4.label': 'Шаг 4',
    'process.step4.title': 'РЕЗУЛЬТАТ',
    'process.step4.desc': 'Получение идеального изделия',
    // Portfolio
    'portfolio.label': 'PORTFOLIO',
    'portfolio.title': 'НАШИ РАБОТЫ',
    'portfolio.all': 'ВСЕ',
    'portfolio.tailoring': 'ПОШИВ',
    'portfolio.fitting': 'ПОСАДКА',
    'portfolio.restoration': 'ВОССТАНОВЛЕНИЕ',
    'portfolio.viewAll': 'ПОСМОТРЕТЬ ВСЕ',
    // Philosophy
    'philosophy.label': 'PHILOSOPHY',
    'philosophy.title': 'ИСКУССТВО',
    'philosophy.subtitle': 'деталей',
    'philosophy.text1': '«На 5 поверхів» — это пространство тишины и эстетики среди городского шума. Мы не просто шьем одежду, мы создаем ощущение уверенности и комфорта.',
    'philosophy.text2': 'Каждый стежок — это диалог между мастером и тканью. Мы верим в медленную моду, качество, неподвластное времени, и индивидуальность, которую невозможно скопировать.',
    'philosophy.founder': 'Алина',
    'philosophy.role': 'Основательница & Креативный Директор',
    // Service Details
    'details.title': 'ПОДРОБНЕЕ ОБ УСЛУГАХ',
    'details.bespoke': 'ИНДИВИДУАЛЬНЫЙ ПОШИВ',
    'details.alterations': 'КОРРЕКЦИЯ ПОСАДКИ',
    'details.restoration': 'РЕМОНТ И РЕСТАВРАЦИЯ',
    'details.bespoke.1': 'Вечерние платья',
    'details.bespoke.2': 'Верхняя одежда',
    'details.bespoke.3': 'Деловые костюмы',
    'details.bespoke.4': 'Изделия из кожи',
    'details.alterations.1': 'Подгонка по фигуре',
    'details.alterations.2': 'Изменение длины',
    'details.alterations.3': 'Коррекция посадки',
    'details.alterations.4': 'Замена фурнитуры',
    'details.restoration.1': 'Реставрация винтажа',
    'details.restoration.2': 'Ремонт трикотажа',
    'details.restoration.3': 'Восстановление швов',
    'details.restoration.4': 'Замена подкладки',
    // Testimonials
    'testimonials.label': 'TESTIMONIALS',
    'testimonials.title': 'ДОВЕРИЕ КЛИЕНТОВ',
    'testimonials.1.text': 'Безупречная работа. Платье село идеально, как вторая кожа.',
    'testimonials.1.author': 'ЕЛЕНА К.',
    'testimonials.1.role': 'Постоянный клиент',
    'testimonials.2.text': 'Спасли мой любимый жакет. Настоящая магия.',
    'testimonials.2.author': 'МАРИНА В.',
    'testimonials.2.role': 'Клиент',
    'testimonials.3.text': 'Высочайший уровень сервиса в городе. Приватно и профессионально.',
    'testimonials.3.author': 'ИРИНА М.',
    'testimonials.3.role': 'VIP Клиент',
    // Contact
    'contact.title': 'КАК НАС НАЙТИ',
    'contact.address.title': 'Адрес',
    'contact.address.full': 'ул. 600-летия, 15, кабинет 1, 5 этаж',
    'contact.address.city': 'г. Винница, Винницкая область, 21021',
    'contact.phone.title': 'Телефон',
    'contact.phone.number': '+38 (097) 000 00 00',
    'contact.schedule.title': 'График работы',
    'contact.schedule.weekdays': 'Пн-Пт: 08:30 - 19:00',
    'contact.schedule.saturday': 'Сб: 15:00 - 21:00',
    'contact.schedule.sunday': 'Вс: Выходной',
    'contact.email.title': 'Email',
    'contact.form.title': 'Записаться на примерку',
    'contact.form.name': 'Имя',
    'contact.form.phone': 'Телефон',
    'contact.form.message': 'Сообщение',
    'contact.form.submit': 'ОТПРАВИТЬ',
    'contact.form.name.placeholder': 'Ваше имя',
    'contact.form.phone.placeholder': '+38 (___) ___ __ __',
    'contact.form.message.placeholder': 'Опишите ваши пожелания...',
    // Footer
    'footer.tagline': 'PREMIUM ATELIER',
    'footer.copyright': '© 2019-2024 На 5 поверхів. Все права защищены.'
  }
};
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('uk');
  const t = (key: string): string => {
    return translations[language][key] || key;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-280">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}